import { Injectable } from '@angular/core';

import {
  findKey,
  get,
  intersectionWith,
  isEmpty,
  join,
  mapValues,
  keyBy,
  isEqual,
  filter,
} from 'lodash';
import { Store } from '@ngxs/store';
import { IQuoteInput, QuoteState } from '@nibtravel/shared/data-access/quote';
import { format } from 'date-fns';
import { CountriesState } from '@nibtravel/shared/data-access/countries';
import {
  IBasicOptionInput,
  IDestinationCountry,
  IHomeCountry,
  IPolicyHoldersInput,
  ISpecifiedItemOptionInput,
  OptionType,
  regionCountryCodeMapping,
} from '@nibtravel/shared/util/models';

@Injectable({
  providedIn: 'root',
})
export class PurchasePathAnalyticsUtilsService {
  constructor(private _store: Store) {}

  countryMappingCache = { result: null };

  getAnalyticsCommonData() {
    const quote = this._store.selectSnapshot(QuoteState.quote);
    const input = get(quote, 'input');
    const policyHolders = get(input, 'policyHolders', []).filter((policy) =>
      Boolean(policy.age)
    );
    const countryList = this._store.selectSnapshot(
      CountriesState.destinationCountriesList
    );
    const homeCountries = this._store.selectSnapshot(
      CountriesState.homeCountries
    );
    const departureDate = format(
      new Date(get(input, 'departureDate', '')),
      'yyyy/MM/dd'
    );
    const returnDate = format(
      new Date(get(input, 'coverEndDate', '')),
      'yyyy/MM/dd'
    );
    const destinationCodes = join(get(input, 'destinationCountryCodes'), ',');
    const configMap = this.getDestinationCountriesCodeMapping(countryList);
    const destinationCountryNames = this.getDestinationCountryNames(
      configMap,
      destinationCodes
    );

    const destinationGroup = this.getDestinationGroup(destinationCodes);
    const homeCountryCode = this.getHomeCountryCode(input);
    const residence = this.getHomeCountryName(homeCountryCode, homeCountries);

    const travellers = (policyHolders.length || 0).toString();
    const t_1_age = this.getPrimaryTravellerAge(policyHolders);
    const t_2_age = this.getSecondaryTravellerAge(policyHolders);
    const t_ages = this.getTravellersAgeRanges(policyHolders);

    return {
      residence,
      destination: destinationCountryNames,
      destination_id: destinationCodes,
      destination_group: destinationGroup,
      date_departure: departureDate,
      date_return: returnDate,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
    };
  }

  getAnalyticAdditionalPolicyData() {
    const quote = this._store.selectSnapshot(QuoteState.quote);
    const quoteInput = get(quote, 'input');

    const hv_sports_gear = this.isHighValueItemSpecified(quoteInput, 'Sport');
    const hv_tech_camera = this.isHighValueItemSpecified(
      quoteInput,
      'Electronic'
    );
    const hv_personal_items = this.isHighValueItemSpecified(
      quoteInput,
      'Other'
    );
    const sport_activity = this.isSnowSportsOptionSelected(quoteInput);
    const program_name = '';
    const program_id = this.getFootprintDonationProjectId(quoteInput);
    const program_amount = this.getFootprintDonationAmount(quoteInput);

    return {
      hv_sports_gear,
      hv_tech_camera,
      hv_personal_items,
      sport_activity,
      program_name,
      program_id,
      program_amount,
    };
  }

  getHomeCountryCode = (quoteInput: any): string => {
    return get(quoteInput, 'homeAddress.countryCode', '');
  };

  getHomeCountryName = (
    countryCode: string,
    homeCountryList: IHomeCountry[]
  ): string => {
    return (
      homeCountryList
        .filter((c) => countryCode.includes(c.code))
        .map((c) => c.name)[0] || ''
    );
  };

  getDestinationGroup(destinationCodes: string) {
    const selectedCountryCodes = destinationCodes
      .split(',')
      .map((code) => code.trim());
    const selectedRegions = selectedCountryCodes.map((code) => {
      const region = findKey(regionCountryCodeMapping, (codes) =>
        codes.includes(code)
      );
      return region ? region : '';
    });
    const commonRegions = intersectionWith(
      selectedRegions,
      Object.keys(regionCountryCodeMapping),
      isEqual
    );
    return commonRegions.join(',');
  }
  getDestinationCountriesCodeMapping(countries: IDestinationCountry[]) {
    if (this.countryMappingCache.result) {
      return this.countryMappingCache.result;
    } else {
      const configMap = mapValues(
        keyBy(countries, 'code'),
        (country) => country
      );

      // @ts-ignore
      this.countryMappingCache.result = configMap;

      return this.countryMappingCache.result;
    }
  }

  getDestinationCountryNames(configMap: any, destinationCodes: string) {
    return destinationCodes
      .split(',')
      .map((code) => configMap[code]?.name)
      .join(',');
  }

  getPrimaryTravellerAge = (policyHolders: IPolicyHoldersInput[]): string => {
    return (
      policyHolders.filter((t) => t.isPrimary === true).map((t) => t.age)[0] ||
      ''
    ).toString();
  };

  getSecondaryTravellerAge = (policyHolders: IPolicyHoldersInput[]): string => {
    return (
      policyHolders
        .filter((t) => t.isPrimary === false && t.isDependent === false)
        .map((t) => t.age)[0] || ''
    ).toString();
  };

  getTravellersAgeRanges = (policyHolders: IPolicyHoldersInput[]): string => {
    const travellersAges = policyHolders.map((t) => t.age) || [0];
    return this.groupAges(travellersAges as number[]);
  };

  /**
   * @param Ages array of traveller ages
   */
  groupAges(Ages: number[]) {
    const sortedAges = Ages.sort((a, b) => a - b);

    const ageRanges = [
      { range: '0-16', minAge: 0, maxAge: 16 },
      { range: '17-25', minAge: 17, maxAge: 25 },
      { range: '26-35', minAge: 26, maxAge: 35 },
      { range: '36-45', minAge: 36, maxAge: 45 },
      { range: '46-55', minAge: 46, maxAge: 55 },
      { range: '56-65', minAge: 56, maxAge: 65 },
      { range: '65+', minAge: 66, maxAge: Infinity },
    ];

    const result = sortedAges.map((age) => {
      const targetAgeRange = ageRanges.find((ageRange) => {
        return age >= ageRange.minAge && age <= ageRange.maxAge;
      });

      const targetAgeRangeValue = get(targetAgeRange, 'range', '');

      return targetAgeRangeValue;
    });

    return result.join(',');
  }

  isHighValueItemSpecified = (
    quoteInput: IQuoteInput | undefined,
    itemType: string
  ): string => {
    const specifiedItems = get(
      quoteInput,
      'specifiedItemOption.specifiedItems',
      []
    ) as ISpecifiedItemOptionInput[];

    const items = filter(specifiedItems, { itemTypeCode: itemType });

    return isEmpty(items) ? 'false' : 'true';
  };

  getFootprintDonationAmount = (quoteInput: any): string => {
    return get(quoteInput, 'footprintsDonation.donationAmount', '0');
  };

  getFootprintDonationProjectId = (quoteInput: any): string => {
    return get(quoteInput, 'footprintsDonation.donationProjectId', '');
  };

  isSnowSportsOptionSelected = (
    quoteInput: IQuoteInput | undefined
  ): string => {
    const specifiedOptions: IBasicOptionInput[] = get(
      quoteInput,
      'basicOptions',
      []
    );

    const snowSportsOption = specifiedOptions.find(
      (option) => option.optionType === OptionType.SnowSports
    );

    return snowSportsOption?.isOptedIn.toString() || 'false';
  };
}
